/*
  Reviews page (T17): one filter for the "Reviews by robot" tiles and the toolbar chips above the list.
  - Choosing a robot (tile or chip) fetches that robot's own Judge.me widget (sections/reviews-product-widget.liquid,
    through the Section Rendering API), shows it in place of All reviews and lets Judge.me fill it in.
    "All robots", or choosing the active robot again, goes back. Tiles and chips always agree (aria-pressed).
  - Sort: Judge.me's own select, tidied every time Judge.me draws it: five options, our labels, its values.
  - Long reviews and replies are cut to 5 lines with a "Read more" button, only where the text overflows.
  - "Showing X of Y reviews" above Load more (All reviews) or the page numbers (a robot's reviews).
  - Write a review: the picker loads the chosen robot's reviews and opens Judge.me's form for it.
  - Anything fails: the tile or picker link opens the robot's page.
  Judge.me redraws parts of its widgets (Load more, sort, tabs), so one MutationObserver keeps all of this current.
  Needs judgeme-write.js (BBJudgeme) on the page first.
*/
class ReviewsFilter extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-rv-list]');
    this.toolbar = this.querySelector('[data-rv-toolbar]');
    this.all = this.querySelector('.jdgm-all-reviews-widget');
    this.target = this.querySelector('[data-rv-slot]');
    this.heading = this.querySelector('[data-rv-heading]');
    this.view = this.querySelector('[data-rv-product]');
    this.allHeading = this.heading ? this.heading.textContent : '';
    this.cache = new Map();
    this.current = '';
    this.text = this.dataset;

    this.tiles = [...this.querySelectorAll('[data-rv-filter]')];
    this.chips = [...this.querySelectorAll('[data-rv-chip]')];
    this.tiles.forEach((tile) => {
      tile.setAttribute('role', 'button');
      tile.setAttribute('aria-pressed', 'false');
      tile.setAttribute('aria-controls', 'all-reviews');
      tile.addEventListener('click', (e) => {
        e.preventDefault();
        this.choose(tile.getAttribute('href'));
      });
    });
    this.chips.forEach((chip) => chip.addEventListener('click', () => this.choose(chip.dataset.rvChip)));

    this.querySelectorAll('[data-rv-write]').forEach((link) => link.addEventListener('click', (e) => {
      e.preventDefault();
      link.closest('details')?.removeAttribute('open');
      this.write(link.getAttribute('href'));
    }));
    document.querySelectorAll('[data-rv-write-open]').forEach((link) => link.addEventListener('click', () => {
      const picker = this.querySelector('[data-rv-picker]');
      if (picker) picker.open = true;
    }));

    // Judge.me switches tabs and lists by style and class, not only by adding nodes
    this.watch = { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] };
    // A new sort or tab makes Judge.me empty the list and refill it: hold its height and keep "All reviews" in view
    this.list.addEventListener('change', (e) => {
      if (e.target.matches('.jdgm-rev-widg__sort-wrapper select')) this.hold();
    }, true);
    this.list.addEventListener('click', (e) => {
      if (e.target.closest('.jdgm-subtab__name, .jdgm-paginate__page')) this.hold();
    }, true);
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(this.list, this.watch);
    window.addEventListener('resize', () => { this.recheck = true; this.schedule(); });
    this.tidy();
  }

  // A robot is its page path: tile links can carry extra query parameters (the staging preview adds one)
  key(href) { return href ? new URL(href, window.location.origin).pathname : ''; }

  // The active robot again, or "All robots" (empty), shows all reviews
  choose(href) {
    const key = this.key(href);
    if (!key || key === this.current) return this.showAll();
    return this.show(href);
  }

  sectionUrl(href) {
    const url = new URL(href, window.location.origin);
    url.hash = '';
    url.searchParams.set('section_id', 'reviews-product-widget');
    return url.toString();
  }

  async show(href, { scroll = true } = {}) {
    const key = this.key(href);
    const tile = this.tiles.find((t) => this.key(t.getAttribute('href')) === key);
    const name = tile?.dataset.name || '';
    if (tile) href = tile.getAttribute('href');
    this.busy(key, true);
    try {
      let html = this.cache.get(key);
      if (!html) {
        const res = await fetch(this.sectionUrl(href));
        if (!res.ok) throw new Error(res.status);
        html = await res.text();
        if (!html.includes('jdgm-review-widget')) throw new Error('No widget');
        this.cache.set(key, html);
      }
      await window.BBJudgeme.ready();
      this.target.innerHTML = html;
      window.BBJudgeme.init(this.target);
      this.current = key;
      this.sync(key);
      if (this.heading) this.heading.textContent = this.heading.dataset.for.replace('[name]', name);
      this.view.href = href.split('#')[0];
      this.view.textContent = this.view.dataset.label.replace('[name]', name);
      this.view.hidden = false;
      this.all.hidden = true;
      this.target.hidden = false;
      this.tidy();
      if (scroll) this.toTop();
      return true;
    } catch (err) {
      console.warn('Reviews filter: opening the robot page instead', err);
      window.location.href = href;
      return false;
    } finally {
      this.busy(key, false);
    }
  }

  showAll() {
    this.current = '';
    this.sync('');
    if (this.heading) this.heading.textContent = this.allHeading;
    this.target.hidden = true;
    this.target.innerHTML = '';
    this.view.hidden = true;
    this.all.hidden = false;
    this.tidy();
    this.toTop();
  }

  async write(href) {
    if (this.key(href) !== this.current && !(await this.show(href, { scroll: false }))) return;
    try {
      await window.BBJudgeme.openForm(this.target);
    } catch (err) {
      console.warn('Write a review: opening the robot page instead', err);
      window.location.href = href;
    }
  }

  sync(key) {
    this.tiles.forEach((t) => t.setAttribute('aria-pressed', String(this.key(t.getAttribute('href')) === key)));
    this.chips.forEach((c) => {
      const on = this.key(c.dataset.rvChip) === key;
      c.setAttribute('aria-pressed', String(on));
      c.classList.toggle('is-on', on);
    });
  }

  busy(key, on) {
    [...this.tiles, ...this.chips].forEach((el) => {
      if (this.key(el.getAttribute('href') || el.dataset.rvChip) === key) el.toggleAttribute('aria-busy', on);
    });
  }

  // Puts the "All reviews" heading just under the header (header.js publishes its height as --header-offset)
  toTop(instant) {
    const anchor = this.heading || this.toolbar;
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-offset')) || 0;
    const top = anchor.getBoundingClientRect().top + window.scrollY - header - 16;
    if (Math.abs(window.scrollY - top) < 4) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top, behavior: reduce || instant ? 'auto' : 'smooth' });
  }

  // Judge.me also scrolls to its widget's top while it reloads, so the heading is put back once the reviews are in
  hold() {
    this.list.style.minHeight = `${this.list.offsetHeight}px`;
    this.holding = Date.now();
    this.toTop(true);
  }

  // Judge.me animates its own scroll (jQuery) after a reload: stop it and keep the heading in place for a second
  anchor() {
    [0, 150, 400, 700, 1000].forEach((ms) => setTimeout(() => {
      try { window.jdgm?.$?.('html, body').stop(true); } catch (e) { /* no jQuery animation running */ }
      this.toTop(true);
    }, ms));
  }

  // Let go once Judge.me has put reviews back (or after 8 s)
  release() {
    if (!this.holding) return;
    const root = this.target.hidden ? this.all : this.target;
    const spinning = [...root.querySelectorAll('.jdgm-spinner')].some((sp) => sp.offsetParent && getComputedStyle(sp).display !== 'none');
    const filled = [...root.querySelectorAll('.jdgm-rev')].some((r) => r.offsetParent);
    if ((filled && !spinning) || Date.now() - this.holding > 8000) {
      this.holding = 0;
      this.anchor();
      setTimeout(() => { if (!this.holding) this.list.style.minHeight = ''; }, 250);
    } else {
      this.schedule();
    }
  }

  schedule() {
    if (this.pending) return;
    this.pending = setTimeout(() => {
      this.pending = null;
      this.tidy();
    }, 50);
  }

  // Runs after every change Judge.me makes inside the list; each step skips what it has already done
  tidy() {
    this.observer?.disconnect();
    this.tidySorts();
    this.tidyTabs();
    this.clampReviews();
    this.tidyButtons();
    this.showing();
    this.release();
    this.recheck = false;
    this.observer?.observe(this.list, this.watch);
  }

  tidySorts() {
    const order = [
      ['most-recent', this.text.sortRecent],
      ['most-helpful', this.text.sortHelpful],
      ['highest-rating', this.text.sortHighest],
      ['lowest-rating', this.text.sortLowest],
      ['with-pictures', this.text.sortPhotos],
    ];
    // Only the sort select: Judge.me gives the review form's name-format select the same class
    this.list.querySelectorAll('.jdgm-rev-widg__sort-wrapper select.jdgm-sort-dropdown').forEach((select) => {
      const values = [...select.options].map((o) => o.value).join(',');
      if (select.dataset.rvTidy === values) return;
      const current = select.value;
      const have = new Map([...select.options].map((o) => [o.value, o]));
      select.replaceChildren(...order.filter(([v]) => have.has(v)).map(([v, label]) => {
        const option = have.get(v);
        option.textContent = label;
        return option;
      }));
      if ([...select.options].some((o) => o.value === current)) select.value = current;
      select.dataset.rvTidy = [...select.options].map((o) => o.value).join(',');
      select.closest('.jdgm-sort-dropdown-wrapper')?.setAttribute('data-label', this.text.sortLabel);
    });
  }

  // Judge.me shows the Shop reviews list but leaves the Product reviews tab marked active, so mark the tab whose list shows
  shopShowing() {
    const shop = this.all.querySelector('.jdgm-shop-reviews__body');
    return Boolean(shop && shop.offsetParent && shop.querySelector('.jdgm-rev'));
  }

  tidyTabs() {
    if (this.all.hidden) return;
    const shop = this.shopShowing();
    this.all.querySelectorAll('.jdgm-subtab__name').forEach((tab) => {
      const on = (tab.dataset.tabname === 'shop-reviews') === shop;
      if (tab.classList.contains('jdgm--active') !== on) tab.classList.toggle('jdgm--active', on);
      tab.setAttribute('aria-pressed', String(on));
    });
  }

  // Each "Read more" button is tied to its text (el.rvMore), because Judge.me can move the text after we add it
  clampReviews() {
    this.list.querySelectorAll('.rv-more').forEach((b) => {
      if (!b.rvTarget?.isConnected || b.rvTarget.rvMore !== b) b.remove();
    });
    this.list.querySelectorAll('.jdgm-rev__body, .jdgm-rev__reply').forEach((el) => {
      let button = el.rvMore?.isConnected ? el.rvMore : null;
      if (button && button.previousElementSibling !== el) el.after(button);
      if (!el.offsetParent || el.classList.contains('is-open')) return;
      if (el.dataset.rvClamp && !this.recheck) return;
      if (!el.textContent.trim()) return;
      el.dataset.rvClamp = '1';
      el.classList.add('rv-clamp');
      const over = el.scrollHeight > el.clientHeight + 2;
      if (over && !button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'rv-more';
        button.textContent = this.text.more;
        button.setAttribute('aria-expanded', 'false');
        button.rvTarget = el;
        el.rvMore = button;
        button.addEventListener('click', () => {
          const open = el.classList.toggle('is-open');
          button.textContent = open ? this.text.less : this.text.more;
          button.setAttribute('aria-expanded', String(open));
        });
        el.after(button);
      } else if (!over && button) {
        button.remove();
        el.rvMore = null;
      }
    });
  }

  tidyButtons() {
    this.list.querySelectorAll('.jdgm-all-reviews__footer a, .jdgm-all-reviews__footer button').forEach((b) => {
      if (!b.classList.contains('btn')) b.classList.add('btn', 'btn--outline');
    });
  }

  // "Showing X of Y reviews": loaded so far in All reviews, or the current page's range in a robot's reviews
  showing() {
    const robot = !this.target.hidden;
    const root = robot ? this.target : this.all;
    let line = this.list.querySelector('[data-rv-showing]');
    if (!line) {
      line = document.createElement('p');
      line.className = 'rv-showing';
      line.dataset.rvShowing = '';
      line.setAttribute('aria-live', 'polite');
    }
    let total = 0;
    let shown = '';
    let anchor;
    if (robot) {
      const widget = root.querySelector('.jdgm-rev-widg');
      total = Number(widget?.dataset.numberOfReviews || 0);
      const revs = root.querySelectorAll('.jdgm-rev-widg__reviews .jdgm-rev').length;
      const page = Number(root.querySelector('.jdgm-paginate__page.jdgm-curt')?.dataset.page || 1);
      const per = Number(root.querySelector('.jdgm-paginate')?.dataset.perPage || revs);
      const from = (page - 1) * per + 1;
      shown = revs > 1 ? `${from}-${from + revs - 1}` : String(from);
      if (!revs || revs >= total) total = 0;
      anchor = root.querySelector('.jdgm-paginate');
    } else {
      const shopTab = this.shopShowing();
      const active = root.querySelector(`.jdgm-subtab__name[data-tabname="${shopTab ? 'shop' : 'product'}-reviews"]`);
      const body = root.querySelector(shopTab ? '.jdgm-shop-reviews__body' : '.jdgm-all-reviews__body');
      const loaded = body ? body.querySelectorAll('.jdgm-rev').length : 0;
      total = Number(active?.querySelector('.jdgm-subtab__count')?.textContent || 0);
      shown = String(loaded);
      if (loaded >= total) total = 0;
      anchor = root.querySelector('.jdgm-all-reviews__footer');
    }
    if (!total || !anchor) { line.hidden = true; return; }
    line.textContent = this.text.showing.replace('[shown]', shown).replace('[total]', total);
    line.hidden = false;
    if (line.nextElementSibling !== anchor) anchor.before(line);
  }
}

if (!customElements.get('reviews-filter')) customElements.define('reviews-filter', ReviewsFilter);
